import React from 'react';
import { motion } from 'framer-motion';
import { 
  ListBulletIcon,
  PlusCircleIcon,
  QrCodeIcon,
  UsersIcon,
  DocumentTextIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

/**
 * QuickActions - Quick action buttons for common tasks with Premium Light Theme
 */
const QuickActions = ({ onActionClick, activeOrdersCount = 0, pendingApplications = 0 }) => {
  const actions = [
    {
      id: 'live-orders',
      label: 'Live Orders',
      icon: ListBulletIcon,
      badge: activeOrdersCount > 0 ? activeOrdersCount : null,
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'add-menu',
      label: 'Add Menu',
      icon: PlusCircleIcon,
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'qr-code',
      label: 'Generate QR',
      icon: QrCodeIcon,
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      id: 'staff',
      label: 'Manage Staff',
      icon: UsersIcon,
      badge: pendingApplications > 0 ? pendingApplications : null,
      color: 'bg-orange-500 hover:bg-orange-600'
    },
    {
      id: 'bills',
      label: "Today's Bills",
      icon: DocumentTextIcon,
      color: 'bg-indigo-500 hover:bg-indigo-600'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Cog6ToothIcon,
      color: 'bg-neutral-500 hover:bg-neutral-600'
    }
  ];

  return (
    <div className="bg-white rounded-[40px] p-10 border border-gray-100 shadow-sm transition-all hover:shadow-xl hover:shadow-gray-200/20">
      <div className="flex items-center justify-between mb-10">
        <div>
           <h3 className="text-2xl font-black text-gray-900 tracking-tight">Quick Actions</h3>
           <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Easily access common tools</p>
        </div>
        <span className="px-4 py-2 bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest rounded-full border border-gray-100">Ready to serve</span>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
        {actions.map((action, index) => (
          <motion.button
            key={action.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, type: "spring" }}
            onClick={() => onActionClick(action.id)}
            className="group relative flex flex-col items-center justify-center p-8 rounded-[32px] border border-gray-100 bg-gray-50/20 hover:bg-white hover:border-orange-200 hover:shadow-2xl hover:shadow-orange-100/30 transition-all duration-500"
          >
            <div className={`w-16 h-16 rounded-2xl mb-6 flex items-center justify-center transition-all duration-500 shadow-sm ${
              action.id === 'live-orders' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-500 group-hover:text-white' :
              action.id === 'add-menu' ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white' :
              action.id === 'qr-code' ? 'bg-purple-50 text-purple-600 group-hover:bg-purple-500 group-hover:text-white' :
              action.id === 'staff' ? 'bg-orange-50 text-orange-600 group-hover:bg-orange-500 group-hover:text-white' :
              action.id === 'bills' ? 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-500 group-hover:text-white' :
              'bg-gray-100 text-gray-900 group-hover:bg-gray-900 group-hover:text-white'
            }`}>
              <action.icon className="h-7 w-7 stroke-[2]" />
            </div>
            
            <span className="text-[10px] font-black text-gray-500 group-hover:text-gray-900 text-center uppercase tracking-widest">
              {action.label}
            </span>
            
            {action.badge && (
              <span className="absolute top-6 right-6 bg-orange-600 text-white text-[10px] font-black rounded-lg h-6 min-w-[24px] px-1.5 flex items-center justify-center border-2 border-white shadow-xl shadow-orange-100 animate-pulse">
                {action.badge}
              </span>
            )}
            
            <div className="absolute bottom-6 w-0 h-1 bg-orange-500 rounded-full transition-all duration-500 group-hover:w-10"></div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default QuickActions;
